import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { documentationApi } from '../api/documentation.api'
import { LinkedDocumentPreviewModal } from '../modules/documentation/LinkedDocumentPreviewModal'
import formStyles from './ui/Form.module.css'

interface DocumentLinkPickerProps {
  projectId: string
  linkedIds: string[]
  onToggle: (documentId: string) => void
  /** Beim Editieren eines Dokuments selbst ausschließen — man kann sich nicht mit sich selbst verlinken. */
  excludeId?: string
}

/** Verknüpft Dokumentation-Einträge mit etwas anderem (Dokument-zu-Dokument oder
 * Aufgabe-zu-Dokument) — Checkbox zum Verknüpfen, Titel wird bei bestehender Verknüpfung
 * klickbar und öffnet den Inhalt zum Lesen/Kopieren in einem Popup. */
export function DocumentLinkPicker({ projectId, linkedIds, onToggle, excludeId }: DocumentLinkPickerProps) {
  const [previewDocId, setPreviewDocId] = useState<string | null>(null)
  const docsQuery = useQuery({ queryKey: ['documentation', 'list', projectId], queryFn: async () => (await documentationApi.list(projectId)).data })

  const docs = (docsQuery.data ?? []).filter((d) => d.id !== excludeId)
  const linkedSet = new Set(linkedIds)

  if (docs.length === 0) {
    return <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>Keine Dokumente in diesem Projekt vorhanden.</span>
  }

  return (
    <>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {docs.map((d) => {
          const checked = linkedSet.has(d.id)
          return (
            <span
              key={d.id}
              className={formStyles.checkboxRow}
              style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: '3px 8px', fontSize: 'var(--text-xs)' }}
            >
              <input type="checkbox" checked={checked} onChange={() => onToggle(d.id)} />
              {checked ? (
                <button
                  type="button"
                  onClick={() => setPreviewDocId(d.id)}
                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--accent-10)', cursor: 'pointer', font: 'inherit' }}
                >
                  {d.title}
                </button>
              ) : (
                d.title
              )}
            </span>
          )
        })}
      </div>

      {previewDocId ? <LinkedDocumentPreviewModal documentId={previewDocId} onClose={() => setPreviewDocId(null)} /> : null}
    </>
  )
}
